import { getConfig } from "@bidbattles/bloks/config";
import {
	type BackendEndpoint,
	type SearchParams,
	createResponseSchema,
	jsonFetch,
	parseCookies,
	parseResponse,
} from "@bidbattles/bloks/lib/rapace/utils";
import { Result } from "@bidbattles/bloks/lib/result";
import { createServerFn, createServerOnlyFn } from "@tanstack/react-start";
import {
	deleteCookie,
	getRequest,
	getRequestHeader,
	setCookie,
} from "@tanstack/react-start/server";
import { z } from "zod/v4";

export const BBIsomorphicAuthClient = {
	async signup(params: {
		first_name: string;
		last_name: string;
		email: string;
		password: string;
		referral_code?: string;
	}): Promise<Result<null>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await backendFetchServerFn({
					data: {
						method: "POST",
						endpoint: "/v1/signup",
						body: { data: params },
					},
				});

				if (!responseResult.ok) {
					return Result.error(new Error(responseResult.error));
				}

				const json = responseResult.data;
				const parseResult = await parseResponse(
					createResponseSchema(z.unknown()),
					json,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(null);
			}),
		);
	},

	async login(params: { email: string; password: string }): Promise<
		Result<null>
	> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await backendFetchServerFn({
					data: {
						method: "POST",
						endpoint: "/v1/login",
						body: { data: params },
					},
				});

				if (!responseResult.ok) {
					return Result.error(new Error(responseResult.error));
				}

				const json = responseResult.data;
				const parseResult = await parseResponse(
					createResponseSchema(z.unknown()),
					json,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(null);
			}),
		);
	},
};

export const backendFetchServerFn = createServerFn({ method: "POST" })
	.inputValidator(
		(
			params: {
				query?: SearchParams;
				body?: Record<string, unknown>;
			} & BackendEndpoint,
		) => params,
	)
	.handler(async (ctx): Promise<Result<object, string>> => {
		return Result.mapErr(
			Result.flatten(
				await Result.tryAsync(async () => {
					const { body, query, ...params } = ctx.data;
					const cookie = getRequestHeader("cookie");
					const result = await jsonFetch({
						...params,
						headers: {
							"content-type": "application/json",
							...(!!cookie && { cookie }),
						},
						body,
						...(query && {
							query: new URLSearchParams(query),
						}),
					});
					if (!result.ok) {
						return result;
					}

					const cookies = result.data.headers.getSetCookie();
					const cookieMap = parseCookies(cookies);
					const newToken = cookieMap.get("token");
					if (newToken) {
						// Safety: Browsers will refuse to set a cookie for a domain that isn't
						// the requesting origin, or a subset of it.
						const requestUrl = new URL(getRequest().url);
						setCookie("token", newToken, {
							httpOnly: useHttpOnlyCookies(),
							secure: true,
							domain: cookieDomain(requestUrl),
						});
					}

					const json = await Result.tryAsync(
						async () => (await result.data.json()) as object,
					);
					return json;
				}),
			),
			(error) => String(error),
		);
	});

export const deleteToken__ServerOnly = createServerOnlyFn(() => {
	// Safety: Browsers will refuse to set a cookie for a domain that isn't
	// the requesting origin, or a subset of it.
	const requestUrl = new URL(getRequest().url);
	deleteCookie("token", {
		httpOnly: useHttpOnlyCookies(),
		secure: true,
		domain: cookieDomain(requestUrl),
	});
});

export function getAuthTokenFromCookie__DEV() {
	if (getConfig().env !== "development") return null;

	const cookieMap = parseCookies(document.cookie.split("; "));
	return cookieMap.get("token") ?? null;
}

const useHttpOnlyCookies = () => getConfig().env !== "development";
const cookieDomain = (url: URL) =>
	getConfig().env === "production"
		? "bidbattles.nl"
		: getConfig().env === "staging"
			? "staging.bidbattles.nl"
			: url.hostname;
